import { App, Button, Tag, Tooltip, type TableColumnsType } from 'antd';
import { Pencil, Trash2 } from 'lucide-react';
import DsIcon from '@/components/Layout/DsIcon';
import { UserName } from '@/components/UserName';
import ProTable from '@/components/pro/ProTable';
import { formatTimestamp } from '@/utils/utils';
import { ApiError } from '@/api/errors';
import { secretData, secretTypeLabel } from '@/lib/secretStorage';
import { type Secret } from '@/proto/agentplatform/v2/secret_pb';
import { useDeleteSecret } from '../hooks/useSecrets';

interface Props {
  data: Secret[];
  loading: boolean;
  onEdit: (row: Secret) => void;
}

export default function ObjectStorageCredentialTable({ data, loading, onEdit }: Props) {
  const { message, modal } = App.useApp();
  const deleteMut = useDeleteSecret();

  function onDelete(row: Secret) {
    modal.confirm({
      title: '删除对象存储凭证',
      content: `确认删除凭证「${row.name}」？若有沙箱正在使用该凭证，将无法再挂载对应的对象存储。`,
      okType: 'danger',
      async onOk() {
        try {
          await deleteMut.mutateAsync(row.id);
          message.success('凭证已删除');
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : (e as Error).message);
        }
      },
    });
  }

  const columns: TableColumnsType<Secret> = [
    {
      title: '凭证名称',
      key: 'name',
      minWidth: 180,
      render: (_, row) => (
        <div className="pp-cell-name">
          <span
            className="obj-icon-tinted"
            style={{ ['--c' as string]: 'var(--platform-c-credential)' }}
          >
            <DsIcon name="database" size={15} />
          </span>
          <div>
            <div className="name-text">{row.name}</div>
            <div className="id-text">{row.id}</div>
          </div>
        </div>
      ),
    },
    {
      title: '存储类型',
      key: 'type',
      minWidth: 140,
      render: (_, r) => (
        <Tag color="processing" bordered={false}>
          {secretTypeLabel(r)}
        </Tag>
      ),
    },
    {
      title: '区域',
      key: 'region',
      minWidth: 120,
      render: (_, r) => <code className="pp-mono">{secretData(r)?.region || '-'}</code>,
    },
    {
      title: '访问地址',
      key: 'endpoint',
      minWidth: 240,
      render: (_, r) => (
        <Tooltip title={secretData(r)?.endpoint}>
          <span className="creds-url">{secretData(r)?.endpoint || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'AccessKey ID',
      key: 'accessKey',
      minWidth: 160,
      render: (_, r) => <code className="pp-mono">{secretData(r)?.accessKey || '-'}</code>,
    },
    {
      title: '创建者',
      key: 'userId',
      minWidth: 100,
      render: (_, r) => <UserName userId={r.userId} />,
    },
    {
      title: '创建时间',
      key: 'createdAt',
      minWidth: 160,
      render: (_, r) => formatTimestamp(r.createdAt),
    },
    {
      title: '操作',
      key: '__actions',
      width: 110,
      align: 'left',
      fixed: 'right',
      render: (_, r) => (
        <>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => onEdit(r)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              size="small"
              icon={<Trash2 size={14} />}
              onClick={() => onDelete(r)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <ProTable<Secret>
      data={data}
      columns={columns}
      rowKey="id"
      size="middle"
      loading={loading}
      scroll={{ x: 'max-content' }}
      emptyChildren={
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-color-placeholder)' }}>
          还没有对象存储凭证 · 点击「新建存储凭证」创建
        </div>
      }
    />
  );
}
